import type { MagiMethod } from "../config";
import type { ModelResponse, StructuredResult } from "../types";

/**
 * Low-profile verdict strip. Keeps the tone professional: a thin coloured
 * border + compact tally, rather than a dramatic full-screen stamp.
 *
 * Also hosts the settled form of the method-specific visualisation
 * (bar tally for Vote*, dot track for Probability) so users get a fully
 * readable summary below the chamber even when node crowding obscured
 * the in-flight overlay.
 */
export function VerdictOverlay({
  method,
  result,
}: {
  method: MagiMethod;
  result: StructuredResult;
}) {
  const round = result.rounds?.at(-1);
  if (!round) return null;
  const agg = round.aggregate ?? {};

  if (method === "VoteYesNo" || method === "VoteOptions") {
    const votes = (agg.votes ?? {}) as Record<string, number>;
    const winner = String(agg.winner ?? "No Majority");
    const variant = variantFor(winner);
    const total = Object.values(votes).reduce((a, b) => a + b, 0);
    return (
      <div className={`verdict ${variant}`} role="status" aria-live="polite">
        <div>
          <div className="meta">Deliberation verdict</div>
          <div className={`stamp ${stampClass(variant)}`}>
            {winner === "No Majority" ? "NO MAJORITY" : winner.toString().toUpperCase()}
          </div>
        </div>
        <div className="tally">
          {Object.entries(votes).map(([k, v]) => (
            <div key={k} className="cell">
              <div className="num">{v}</div>
              <div className="lbl">{k}</div>
            </div>
          ))}
          {total > 0 && (
            <div className="cell">
              <div className="num">{total}</div>
              <div className="lbl">Total</div>
            </div>
          )}
        </div>
        {total > 0 && <VoteTallyBars votes={votes} total={total} method={method} />}
      </div>
    );
  }

  if (method === "Probability") {
    const avg = typeof agg.average === "number" ? (agg.average as number) : 0;
    const med = typeof agg.median === "number" ? (agg.median as number) : 0;
    const min = typeof agg.min === "number" ? (agg.min as number) : 0;
    const max = typeof agg.max === "number" ? (agg.max as number) : 0;
    return (
      <div className="verdict" role="status" aria-live="polite">
        <div style={{ flex: 1 }}>
          <div className="meta">Probability estimate</div>
          <div className="stamp glow-amb" style={{ color: "var(--accent)" }}>
            {(avg * 100).toFixed(1)}%
          </div>
          <div className="gauge" style={{ marginTop: 10 }}>
            <div className="fill" style={{ width: `${Math.max(0, Math.min(1, avg)) * 100}%` }} />
          </div>
        </div>
        <div className="tally">
          <Stat label="Median" val={`${(med * 100).toFixed(0)}%`} />
          <Stat label="Min" val={`${(min * 100).toFixed(0)}%`} />
          <Stat label="Max" val={`${(max * 100).toFixed(0)}%`} />
        </div>
        <ProbabilityTrack responses={round.responses} average={avg} />
      </div>
    );
  }

  if (method === "Compose") {
    const ranked = (agg.ranked_candidates ?? []) as Array<Record<string, unknown>>;
    const top = ranked[0];
    const topScore =
      typeof top?.average_score === "number" ? (top.average_score as number) : null;
    return (
      <div className="verdict" role="status" aria-live="polite">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="meta">Top-ranked candidate</div>
          <div
            className="stamp glow-amb mono"
            style={{
              color: "var(--accent)",
              fontSize: 18,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={String(top?.model ?? "—")}
          >
            {String(top?.model ?? "—")}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 4 }}>
            Full ranking + peer reviews below.
          </div>
        </div>
        <div className="tally">
          <Stat
            label="Top score"
            val={topScore !== null ? topScore.toFixed(2) : "—"}
          />
          <Stat label="Candidates" val={String(ranked.length)} />
        </div>
      </div>
    );
  }

  // Majority / Consensus / Minority / Synthesis — no numeric aggregate,
  // so a single-line status strip.
  return (
    <div className="verdict" role="status" aria-live="polite">
      <div>
        <div className="meta">Deliberation</div>
        <div className="stamp glow-amb" style={{ color: "var(--accent)", fontSize: 18 }}>
          COMPLETE · {method.toUpperCase()}
        </div>
      </div>
      <div className="tally">
        <Stat label="Models" val={String(round.responses.length)} />
      </div>
    </div>
  );
}

function Stat({ label, val }: { label: string; val: string }) {
  return (
    <div className="cell">
      <div className="num">{val}</div>
      <div className="lbl">{label}</div>
    </div>
  );
}

function variantFor(winner: string): "positive" | "negative" | "" {
  const w = winner.toLowerCase();
  if (w === "yes") return "positive";
  if (w === "no") return "negative";
  return "";
}
function stampClass(v: string): string {
  if (v === "positive") return "pos glow-pos";
  if (v === "negative") return "neg glow-neg";
  return "amb glow-amb";
}

const VOTE_OPTION_PALETTE = [
  "var(--accent)",
  "var(--positive)",
  "var(--neutral)",
  "var(--negative)",
  "var(--accent-dim)",
];

function voteColor(label: string, method: MagiMethod, index: number): string {
  if (method === "VoteYesNo") {
    const l = label.toLowerCase();
    if (l === "yes") return "var(--positive)";
    if (l === "no") return "var(--negative)";
    return "var(--accent)";
  }
  return VOTE_OPTION_PALETTE[index % VOTE_OPTION_PALETTE.length];
}

function VoteTallyBars({
  votes,
  total,
  method,
}: {
  votes: Record<string, number>;
  total: number;
  method: MagiMethod;
}) {
  const entries =
    method === "VoteYesNo"
      ? Object.entries(votes)
      : [...Object.entries(votes)].sort((a, b) => b[1] - a[1]);
  return (
    <div className="verdict-viz" data-testid="verdict-tally-bars">
      {entries.map(([label, n], i) => {
        const pct = (n / total) * 100;
        const color = voteColor(label, method, i);
        return (
          <div key={label} className="verdict-viz-row">
            <span className="verdict-viz-label" style={{ color }} title={label}>
              {label.toUpperCase()}
            </span>
            <div className="verdict-viz-track">
              <div
                className="tally-bar"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <span className="verdict-viz-count">{n}</span>
          </div>
        );
      })}
    </div>
  );
}

function ProbabilityTrack({
  responses,
  average,
}: {
  responses: ModelResponse[];
  average: number;
}) {
  const values = responses
    .map((r) => (typeof r.response === "number" ? r.response : Number(r.response)))
    .filter((v): v is number => Number.isFinite(v) && v >= 0);
  if (values.length === 0) return null;
  return (
    <div className="verdict-viz" data-testid="verdict-probability-track">
      <div className="verdict-prob-track">
        {values.map((v, i) => (
          <div
            key={i}
            className="verdict-prob-dot"
            style={{
              left: `${Math.max(0, Math.min(1, v)) * 100}%`,
              background: "var(--accent)",
            }}
            title={`${(v * 100).toFixed(1)}%`}
          />
        ))}
        <div
          className="verdict-prob-dot verdict-prob-avg"
          style={{ left: `${Math.max(0, Math.min(1, average)) * 100}%` }}
          title={`Average ${(average * 100).toFixed(1)}%`}
        />
      </div>
      <div className="verdict-prob-scale">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
    </div>
  );
}
